import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { AuthGateway } from '@domain/ports/auth-gateway'
import type {
  AccountRepository,
  CategoryRepository,
  CreditCardRepository,
  TransactionRepository,
  BreakdownRepository,
  CommentRepository,
  RecadoRepository,
  NotificationRepository,
  ProductRepository,
  InvoiceRepository,
  ProviderAccountRepository,
} from '@domain/ports/finance-repository'
import type { HouseholdRepository } from '@domain/ports/household-repository'
import { AuthProvider } from '@ui/auth/AuthProvider'
import { FinanceRepositoriesContext } from '@ui/finance/finance-context'
import type { ListRepository } from '@domain/ports/list-repository'
import type { ReceiptRepository } from '@domain/ports/receipt-repository'
import type { AgentInstructionsRepository } from '@domain/ports/agent-instructions-repository'
import type { CategoryRuleRepository } from '@domain/ports/category-rule-repository'
import type { ProductLookup } from '@domain/ports/product-lookup'
import { HouseholdRepositoryContext } from '@ui/household/household-context'

// `staleTime` de 30s e uma retentativa: herdado da Fase 0, ainda sem consumidor.
// Ver a decisão 70 — a Fase 1 leu servidor pela primeira vez e escreveu estado à
// mão em `use-members.ts` em vez de usar isto.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
})

type Props = {
  authGateway: AuthGateway
  householdRepository: HouseholdRepository
  accountRepository: AccountRepository
  creditCardRepository: CreditCardRepository
  categoryRepository: CategoryRepository
  transactionRepository: TransactionRepository
  providerAccountRepository: ProviderAccountRepository
  invoiceRepository: InvoiceRepository
  breakdownRepository: BreakdownRepository
  commentRepository: CommentRepository
  recadoRepository: RecadoRepository
  notificationRepository: NotificationRepository
  productRepository: ProductRepository
  listRepository: ListRepository
  receiptRepository: ReceiptRepository
  agentInstructionsRepository: AgentInstructionsRepository
  categoryRuleRepository: CategoryRuleRepository
  productLookup: ProductLookup
  children: ReactNode
}

/**
 * **A árvore de providers, escrita uma vez só.**
 *
 * Ela existia duplicada: `src/main.tsx` montava uma, e o `renderApp` de
 * `src/test/auth-fixtures.tsx` montava outra igual. Duas cópias da mesma
 * composição significam que esquecer um provider na de produção não quebra
 * teste nenhum — os testes montam a **outra**, completa — e o erro só aparece
 * em runtime, no `throw` de `useHouseholdRepository`.
 *
 * Com as duas apontando para cá, cada porta é uma **prop obrigatória**: omitir
 * uma é erro de `tsc`, não bug de produção. Valia para as duas portas da Fase 1
 * e vale para as quatro do núcleo financeiro, que entraram na Task 8 — e é por
 * isso que elas são seis props, e não um objeto só: um objeto opcional voltaria
 * a deixar a omissão passar.
 *
 * As portas chegam como props, e não são buscadas aqui: a composição real
 * continua sendo `src/main.tsx`, o único arquivo que pode importar `adapters/`.
 *
 * O `QueryClientProvider` mora aqui pelo mesmo motivo, e a revisão final da
 * Fase 1 mostrou por quê: ele tinha ficado FORA deste componente, direto no
 * `main.tsx`, então o `renderApp` do teste nunca o montava — a mesma condição
 * de duas árvores que este arquivo existe para acabar, no mesmo arquivo, depois
 * de eu declarar a família fechada. Apagá-lo daqui não quebraria teste nenhum
 * hoje (não há um `useQuery` no repositório); dentro da árvore compartilhada,
 * pelo menos toda tela em teste roda sob o mesmo provedor que a de produção.
 */
export function AppProviders({
  authGateway,
  householdRepository,
  accountRepository,
  creditCardRepository,
  categoryRepository,
  transactionRepository,
  providerAccountRepository,
  invoiceRepository,
  breakdownRepository,
  commentRepository,
  recadoRepository,
  notificationRepository,
  productRepository,
  listRepository,
  receiptRepository,
  agentInstructionsRepository,
  categoryRuleRepository,
  productLookup,
  children,
}: Props) {
  // As quatro portas viram um objeto só para o contexto, e o `useMemo` existe
  // para que esse objeto não seja novo a cada render — um valor de contexto
  // recriado remonta todo consumidor abaixo, e as portas nunca mudam depois da
  // composição.
  const finance = useMemo(
    () => ({
      accountRepository,
      creditCardRepository,
      categoryRepository,
      transactionRepository,
      providerAccountRepository,
      invoiceRepository,
      breakdownRepository,
      commentRepository,
      recadoRepository,
      notificationRepository,
      productRepository,
      listRepository,
      receiptRepository,
      agentInstructionsRepository,
      categoryRuleRepository,
      productLookup,
    }),
    [
      accountRepository,
      creditCardRepository,
      categoryRepository,
      transactionRepository,
      providerAccountRepository,
      invoiceRepository,
      breakdownRepository,
      commentRepository,
      recadoRepository,
      notificationRepository,
      productRepository,
      listRepository,
      receiptRepository,
      agentInstructionsRepository,
      categoryRuleRepository,
      productLookup,
    ],
  )

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider gateway={authGateway}>
        <HouseholdRepositoryContext.Provider value={householdRepository}>
          <FinanceRepositoriesContext.Provider value={finance}>
            {children}
          </FinanceRepositoriesContext.Provider>
        </HouseholdRepositoryContext.Provider>
      </AuthProvider>
    </QueryClientProvider>
  )
}
